import { isAccessDeniedError } from '@/api';
import { flattenCursorPages } from '@/api/cursorPagination';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, EmptyState, MaskedValue } from '@/components';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { cn } from '@/lib';
import { formatDateTime } from '@/utils/dateUtils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeviceUserLevelKeyHistory } from '../../hooks';
import type { UserLevelKeyHistoryEntry, VisibleUserLevel } from '../../types';
import { DeviceTabLoading } from '../DeviceTabStates';

export interface UserLevelKeyHistoryDialogProps {
  deviceId: string;
  // Level whose history is shown; null keeps the dialog closed and the query idle.
  userLevel: VisibleUserLevel | null;
  onClose: () => void;
}

// UserLevelKeyHistoryDialog lists past keys of one user level, newest first, with endless
// scroll inside the dialog body. SECURITY: values are plaintext PINs; they are masked by
// default and dropped from memory when the dialog closes.
export function UserLevelKeyHistoryDialog({ deviceId, userLevel, onClose }: UserLevelKeyHistoryDialogProps) {
  const { t } = useTranslation();
  const isOpen = userLevel !== null;

  return (
    <Dialog isOpen={isOpen} onClose={onClose} className="max-w-lg">
      <DialogHeader showClose>
        {t('devices.detail.rules.userLevelKeys.historyTitle', { level: userLevel ?? '' })}
      </DialogHeader>
      {isOpen && <HistoryList deviceId={deviceId} userLevel={userLevel} />}
      <DialogFooter>
        <Button type="button" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface HistoryListProps {
  deviceId: string;
  userLevel: VisibleUserLevel;
}

// HistoryList is mounted only while the dialog is open so its query (and the PINs in it)
// exist no longer than the popup itself.
function HistoryList({ deviceId, userLevel }: HistoryListProps) {
  const { t } = useTranslation();
  const historyQuery = useDeviceUserLevelKeyHistory(deviceId, userLevel);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = historyQuery;
  const sentinelRef = useInfiniteScrollSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin: '120px 0px' });

  const entries = useMemo(() => flattenCursorPages(historyQuery.data?.pages), [historyQuery.data]);
  const hasReachedEnd = !hasNextPage && !historyQuery.isLoading && entries.length > 0;

  return (
    <DialogBody className="pt-4 pb-2">
      <p className="text-muted mb-3 text-sm">{t('devices.detail.rules.userLevelKeys.historyDescription')}</p>
      {/* Scrollable list */}
      <div className="max-h-[min(56vh,440px)] overflow-y-auto pr-1">
        <HistoryRows entries={entries} isLoading={historyQuery.isLoading} error={historyQuery.error} />
        {isFetchingNextPage && <DeviceTabLoading rows={2} height={60} className="mt-2 gap-2" />}
        <div ref={sentinelRef} aria-hidden="true" />
        {hasReachedEnd && (
          <p className="text-muted py-4 text-center text-xs">{t('devices.detail.rules.userLevelKeys.historyEnd')}</p>
        )}
      </div>
    </DialogBody>
  );
}

interface HistoryRowsProps {
  entries: UserLevelKeyHistoryEntry[];
  isLoading: boolean;
  error: unknown;
}

// HistoryRows renders loading, no-access, empty or the list of past keys.
function HistoryRows({ entries, isLoading, error }: HistoryRowsProps) {
  const { t } = useTranslation();

  if (isLoading) return <DeviceTabLoading rows={2} height={60} className="mt-2 gap-2" />;
  if (isAccessDeniedError(error)) return <EmptyState title={t('devices.detail.rules.userLevelKeys.accessDenied')} className="py-6" />;
  if (error) return <EmptyState title={t('devices.detail.rules.userLevelKeys.error')} className="py-6" />;
  if (entries.length === 0) return <EmptyState title={t('devices.detail.rules.userLevelKeys.historyEmpty')} className="py-6" />;

  return (
    <ol className="flex flex-col gap-2">
      {entries.map((entry, index) => (
        <HistoryRow key={`${entry.validFrom ?? 'unknown'}-${index}`} entry={entry} isCurrent={index === 0} />
      ))}
    </ol>
  );
}

interface HistoryRowProps {
  entry: UserLevelKeyHistoryEntry;
  isCurrent: boolean;
}

// HistoryRow shows one past key with its valid-from date; the newest row is marked current.
function HistoryRow({ entry, isCurrent }: HistoryRowProps) {
  const { t } = useTranslation();
  const validFromLabel = entry.validFrom ? formatDateTime(entry.validFrom) : t('common.notSet');
  const tagLabel = isCurrent
    ? t('devices.detail.rules.userLevelKeys.current')
    : t('devices.detail.rules.userLevelKeys.rotated');

  return (
    <li
      className={cn(
        'flex flex-wrap items-center justify-between gap-4 rounded-lg px-3.5 py-3',
        isCurrent ? 'border-primary bg-primary/5 border-2' : 'border-border bg-surface border'
      )}
    >
      <div className="min-w-0">
        <MaskedValue value={String(entry.key)} variant="inline" />
        <div className="text-muted mt-1 text-xs">{validFromLabel}</div>
      </div>
      <span
        className={cn(
          'flex-none text-xs',
          isCurrent ? 'text-primary font-bold tracking-wider uppercase' : 'text-muted'
        )}
      >
        {tagLabel}
      </span>
    </li>
  );
}
